/**
 * The whole arc on one page: the phase this student last ran, then each phase
 * suggested to follow it, side by side.
 *
 * A suggestion on its own cannot be judged — the question a coach asks is not
 * "is this a reasonable phase" but "why is this the phase that comes after that
 * one". Showing one transition at a time answered that question twice and made
 * the reader hold the first answer in their head while reading the second. Every
 * step is on screen at once, left to right, so the arc is read rather than
 * remembered.
 *
 * Three readings, because the reviewer needs all three:
 *   - set totals per movement pattern, so a shift in emphasis is visible
 *   - exercise by exercise, session against session and BLOCK against block,
 *     because a block is the unit a coach programs in — a flat list of a
 *     session's exercises is not the thing that was designed
 *   - what the validator objected to, against the numbers it objected about
 *
 * Validation is not a separate screen. A breached floor marks the pattern row
 * it breached, so the objection arrives attached to the figure that caused it
 * rather than on a tab the reader has to think to open.
 *
 * Both sides are counted in one vocabulary. A suggested phase's totals are the
 * designer's own `balance`; a past phase's are derived in the sample builder
 * from its movement tags using the same classifier map. What the classifier does
 * not recognise lands in `unclassified` and is said out loud rather than folded
 * into a flattering bucket.
 *
 * Where the two sides know different things, the page shows the asymmetry rather
 * than hiding it: a past phase records set counts only, so reps, load, rationale
 * and basis appear on the suggested side alone.
 */

import { useState } from "react";
import type {
  AuditCheck, BasisResolution, PastPhase, Phase, RuleProvenance, Variant,
} from "./types";
import { BasisChip } from "./Basis";
import { Tip } from "./Tip";
import {
  RegenerateButton, RegenerateOutcome, targetLabel, useRegenerate,
  type RegenTarget,
} from "./Regenerate";

/* ------------------------------------------------------------------ a side -- */

interface Line {
  name: string;
  sets: number | null;
  reps?: number | null;
  load?: string | null;
  rationale?: string | null;
  basis?: BasisResolution | null;
  state?: "kept" | "new" | "dropped";
}

interface SideBlock { name: string; maxSet: number | null; exercises: Line[] }
interface SideWorkout { name: string; blocks: SideBlock[] }

/** One column of the comparison, whichever kind of phase it came from. */
interface Side {
  key: string;
  label: string;
  /** The designer's own reason for this phase's shape, where it gave one. */
  why?: string | null;
  focus?: string | null;
  workouts: SideWorkout[];
  sets?: Record<string, number>;
  coreFunctions?: string[];
  /** A past phase carries no reps, load or reasoning, and says so instead of
   *  implying none. */
  detailed: boolean;
  /** The position this phase holds in the arc being designed. Absent on a past
   *  phase, which is on the record and not up for regeneration. */
  arcPosition?: number;
}

const norm = (s: string) => s.toLowerCase().replace(/\s+/g, " ").trim();

function sideFromSuggested(
  phase: Phase, balance?: Record<string, number>, coreFunctions?: string[],
  labelPosition?: number,
): Side {
  return {
    key: `phase-${phase.position}`,
    // Numbered where it will land in the student's program, so the heading
    // reads as the continuation it is rather than restarting at one.
    label: `Phase ${labelPosition ?? phase.position}`,
    arcPosition: phase.position,
    why: phase.phaseRationale,
    focus: phase.stimulusFocus,
    detailed: true,
    sets: balance,
    coreFunctions,
    workouts: phase.workouts.map(w => ({
      name: w.name,
      blocks: w.blocks.map(b => ({
        name: b.name,
        maxSet: b.maxSet,
        exercises: b.exercises.map(e => ({
          name: e.name,
          sets: e.setCount ?? b.maxSet ?? null,
          reps: e.targetReps,
          load: e.seededWeight != null
            ? `${e.seededWeight}${e.weightUnit || ""}` : null,
          rationale: e.rationale,
          basis: e.basisResolution,
        })),
      })),
    })),
  };
}

function sideFromPast(past: PastPhase): Side {
  return {
    key: `past-${past.position}`,
    label: past.label,
    detailed: false,
    sets: past.balance?.sets,
    coreFunctions: past.balance?.core_functions,
    workouts: (past.workouts || []).map(w => ({
      name: w.name || "",
      blocks: (w.blocks || []).map(b => ({
        name: b.name || "",
        maxSet: null,
        exercises: (b.exercises || []).map(e => ({
          name: e.name, sets: e.setCount,
        })),
      })),
    })),
  };
}

const namesOf = (s: Side) => new Set(
  s.workouts.flatMap(w => w.blocks.flatMap(b => b.exercises.map(e => norm(e.name)))));

/**
 * Mark every exercise against the column to its left.
 *
 * Each phase has to earn its difference from the one before it, so `new` and
 * `kept` are always relative to the immediately preceding column. The first
 * column has nothing to its left, so its exercises are marked by whether the
 * next column carries them forward — otherwise the reader would have to diff
 * two lists by eye to see what was let go.
 */
function paint(sides: Side[]): Side[] {
  const names = sides.map(namesOf);
  return sides.map((s, i) => {
    const before = i > 0 ? names[i - 1] : null;
    const after = i === 0 ? names[1] : null;
    const state = (n: string): Line["state"] => {
      if (before) return before.has(norm(n)) ? "kept" : "new";
      if (after) return after.has(norm(n)) ? "kept" : "dropped";
      return "kept";
    };
    return {
      ...s,
      workouts: s.workouts.map(w => ({
        ...w,
        blocks: w.blocks.map(b => ({
          ...b,
          exercises: b.exercises.map(e => ({ ...e, state: state(e.name) })),
        })),
      })),
    };
  });
}

/* ------------------------------------------------------- what the gate said -- */

/** Pattern keys the validator itself named in a violation, so the table flags
 *  only what the run actually complained about. Both forms the floor check
 *  emits are matched — a pattern that came in short, and one that is absent
 *  altogether, which is the case most worth marking. */
function flaggedPatterns(checks: AuditCheck[]): Set<string> {
  const out = new Set<string>();
  for (const c of checks) {
    for (const v of c.violations) {
      const m = v.match(/([a-z-]+) (?:has \d+ weekly sets|is absent)/i);
      if (m) out.add(m[1].replace(/-/g, "_"));
    }
  }
  return out;
}

/** A check id is stored snake_case; it is read as a name. */
const checkName = (id: string) => {
  const words = id.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** The rule a gate enforces, shown wherever the gate is. A verdict a coach
 *  cannot trace to a rule is one they can only take on trust — the head coach
 *  asked which part of the manual a decision came from, not merely that one
 *  existed. */
function RuleCite({ rule }: { rule?: RuleProvenance }) {
  if (!rule || rule.kind === "unsourced" || rule.cards.length === 0) return null;
  const card = rule.cards[0];
  const more = rule.cards.length - 1;
  const label = rule.kind === "integrity"
    ? "Integrity check"
    : (card.id || "").replace(/^[a-z_]+\./, "");
  const detail = [card.rule, card.manualRef && `Manual: ${card.manualRef}`,
                  card.note].filter(Boolean).join("\n\n");
  return (
    <Tip text={detail || label}>
      <span className={`pr-rule-cite ${rule.kind === "integrity" ? "is-integrity" : ""}`}>
        {label}{more > 0 ? ` +${more}` : ""}
      </span>
    </Tip>
  );
}

function Findings({ variant }: { variant: Variant }) {
  const flags = variant.checks.filter(c => !c.passed || c.violations.length > 0);
  const clean = variant.checks.filter(c => c.passed && c.violations.length === 0);

  return (
    <>
      {flags.map(c => (
        <div className="pr-flag" key={c.check}>
          <strong>{checkName(c.check)}</strong>
          <RuleCite rule={c.rule} />
          {c.passed && c.violations.length > 0 && (
            <Tip text="The checker recorded this as passed even though it also recorded the breach below.">
              {" — reported as passed"}
            </Tip>
          )}
          {c.violations.map(v => <div key={v}>{v}</div>)}
        </div>
      ))}

      {variant.repairs.map(r => (
        <div className="pr-flag" key={r}>
          <strong>Repaired automatically</strong>
          <div>{r}</div>
        </div>
      ))}

      <details className="pr-disclosure">
        <summary>
          {flags.length === 0 && variant.repairs.length === 0
            ? `Every check passed with nothing flagged — all ${clean.length}`
            : `${clean.length} checks passed cleanly`}
        </summary>
        <div className="pr-checks" style={{ marginTop: 8 }}>
          {clean.map(c => (
            <div className="pr-check" key={c.check}>
              <span className="pr-tick pr-pass">✓</span>
              <span>{checkName(c.check)}</span>
              <RuleCite rule={c.rule} />
            </div>
          ))}
        </div>
      </details>
    </>
  );
}

/* -------------------------------------------------------------------- parts -- */

function ExerciseLine({ line, detailed }: { line: Line; detailed: boolean }) {
  const detail = [
    line.sets ? `${line.sets} set` : null,
    detailed && line.reps ? `${line.reps} reps` : null,
    detailed && line.load ? line.load : null,
  ].filter(Boolean).join(" · ");
  return (
    <div className={`pr-cmp-line pr-cmp-${line.state || "kept"}`}>
      {line.rationale
        ? <Tip className="pr-cmp-ex" text={line.rationale}>{line.name}</Tip>
        : <span className="pr-cmp-ex">{line.name}</span>}
      <span className="pr-cmp-tail">
        {line.basis ? <BasisChip basis={line.basis} /> : null}
        {detail ? <span className="pr-cmp-sets">{detail}</span> : null}
      </span>
    </div>
  );
}

/** One side's take on one session, in the blocks it was actually programmed in.
 *  Blocks are not aligned across columns — a phase may pair what the one before
 *  it trisetted, and forcing a row-for-row match would invent a correspondence
 *  the design does not have. */
function SessionColumn({ workout, detailed, onAsk, busyBlock }: {
  workout?: SideWorkout;
  detailed: boolean;
  /** Absent on a past phase — the record is not up for regeneration. */
  onAsk?: (block: number) => void;
  busyBlock?: number | null;
}) {
  if (!workout || workout.blocks.length === 0) {
    return <div className="pr-cmp-none">Nothing here</div>;
  }
  return (
    <>
      {workout.blocks.map((b, i) => (
        <div className="pr-cmp-block" key={`${b.name}-${i}`}>
          <div className="pr-cmp-block-head">
            <span className="pr-cmp-block-name">{b.name || `Block ${i + 1}`}</span>
            {b.maxSet != null && (
              <span className="pr-set-count">{`(${b.maxSet} set)`}</span>
            )}
            {onAsk && (
              <RegenerateButton
                busy={busyBlock === i + 1}
                label={b.name || `Block ${i + 1}`}
                onClick={() => onAsk(i + 1)}
              />
            )}
          </div>
          {b.exercises.map((l, j) => (
            <ExerciseLine detailed={detailed} key={`${l.name}-${j}`} line={l} />
          ))}
        </div>
      ))}
    </>
  );
}

const PATTERNS: { key: string; label: string; sub?: boolean; total?: boolean }[] = [
  { key: "knee_dominant", label: "Knee dominant" },
  { key: "hip_dominant", label: "Hip dominant" },
  { key: "unilateral_lower", label: "…of which unilateral", sub: true },
  { key: "upper_push", label: "Upper push" },
  { key: "horizontal_push", label: "…horizontal", sub: true },
  { key: "vertical_push", label: "…vertical", sub: true },
  { key: "upper_pull", label: "Upper pull" },
  { key: "horizontal_pull", label: "…horizontal", sub: true },
  { key: "vertical_pull", label: "…vertical", sub: true },
  { key: "core", label: "Core" },
  { key: "unclassified", label: "Unclassified" },
  { key: "total", label: "Total sets", total: true },
];

function Delta({ from, to }: { from: number; to: number }) {
  const d = to - from;
  if (!d) return <span className="pr-cmp-delta pr-cmp-flat">same</span>;
  return (
    <span className={`pr-cmp-delta ${d > 0 ? "pr-cmp-up" : "pr-cmp-down"}`}>
      {d > 0 ? `+${d}` : d}
    </span>
  );
}

function SetTable({ sides, flagged }: { sides: Side[]; flagged: Set<string> }) {
  const counted = sides.filter(s => s.sets);
  if (counted.length < 2) {
    const missing = sides.filter(s => !s.sets).map(s => s.label);
    return (
      <div className="pr-panel pr-empty">
        {`${missing.join(" and ")} carries no set counts, so the arc cannot be
          compared by volume — only exercise by exercise, below.`}
      </div>
    );
  }
  return (
    <div className="pr-panel">
      <h2 className="pr-h2">Sets by movement pattern</h2>
      <div className="pr-cmp-table-wrap">
        <table className="pr-cmp-table">
          <thead>
            <tr>
              <th>Pattern</th>
              {sides.map(s => <th key={s.key}>{s.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {PATTERNS.map(p => {
              const values = sides.map(s => s.sets?.[p.key] ?? 0);
              if (values.every(v => !v)) return null;
              const cls = [
                p.total ? "pr-cmp-total" : "",
                p.sub ? "pr-cmp-sub" : "",
                flagged.has(p.key) ? "pr-cmp-breached" : "",
              ].filter(Boolean).join(" ");
              return (
                <tr className={cls} key={p.key}>
                  <th scope="row">
                    {p.label}
                    {flagged.has(p.key) && (
                      <Tip text="The validator reported this pattern below the manual's weekly floor. The objection is written out under Findings.">
                        {/* Labelled, so the row is not read as an exclamation.
                            The sentence itself lives under Findings. */}
                        <span
                          aria-label="below the manual's weekly floor"
                          className="pr-cmp-breach-mark"
                          role="img"
                        >
                          !
                        </span>
                      </Tip>
                    )}
                  </th>
                  {values.map((v, i) => (
                    <td key={sides[i].key}>
                      {sides[i].sets ? v : "—"}
                      {i > 0 && sides[i].sets && sides[i - 1].sets
                        ? <> <Delta from={values[i - 1]} to={v} /></> : null}
                    </td>
                  ))}
                </tr>
              );
            })}
            <tr className="pr-cmp-core-row">
              <th scope="row">Core functions</th>
              {sides.map(s => (
                <td key={s.key}>
                  {s.coreFunctions?.length ? s.coreFunctions.join(", ") : "—"}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      {sides.some(s => (s.sets?.unclassified ?? 0) > 0) && (
        <p className="pr-muted pr-cmp-caveat">
          Sets under “Unclassified” carry a movement tag the classifier does not
          recognise. They are counted in the total and nowhere else.
        </p>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------------- tab -- */

export function CompareTab({ variant, pastPhases, instance, student, canWrite }: {
  variant: Variant;
  pastPhases: PastPhase[];
  instance: string;
  student: string;
  /** Regenerating runs the designer, so it is offered only to a writer. */
  canWrite: boolean;
}) {
  const regen = useRegenerate(instance, student);
  // The arc as regenerated, once it has been. The published sample is left
  // alone — a reroll is a proposal until the page is regenerated from it.
  const [arc, setArc] = useState<Phase[] | null>(null);
  const previous = [...(pastPhases || [])]
    .sort((a, b) => (b.position ?? 0) - (a.position ?? 0))[0];
  // The arc's own 1..n positions stay the key for balance and for addressing a
  // phase; only what the coach reads is renumbered.
  const lastRan = previous?.position ?? 0;

  const sides: Side[] = paint([
    ...(previous?.workouts?.length ? [sideFromPast(previous)] : []),
    ...(arc || variant.phases).map(p => {
      const b = variant.balance.find(x => x.phase === p.position);
      return sideFromSuggested(p, b?.sets, b?.core_functions,
                               lastRan ? lastRan + p.position : undefined);
    }),
  ]);

  if (sides.length < 2) {
    if (!previous) {
      return (
        <div className="pr-panel pr-empty">
          No earlier phase is on file for this student, and this suggestion is a
          single phase — there is nothing to compare it against.
        </div>
      );
    }
    return (
      <div className="pr-panel pr-empty">
        {`${previous.label} is on file, but this sample was published before past
          phases carried their exercises. Regenerate the page to compare against it.`}
      </div>
    );
  }

  const labelFor = (arcPosition: number) =>
    sides.find(s => s.arcPosition === arcPosition)?.label
    ?? `Phase ${arcPosition}`;

  /** One click asks for that node again. */
  const ask = async (target: RegenTarget) => {
    const out = await regen.run(arc || variant.phases, target, "");
    if (out?.arc && out.changed) setArc(out.arc);
  };

  const busyOn = (arcPosition: number, block?: number) =>
    regen.busy?.phase === arcPosition
    && (block == null ? regen.busy?.block == null : regen.busy?.block === block);

  const flagged = flaggedPatterns(variant.checks);
  const sessions = Math.max(...sides.map(s => s.workouts.length));
  const cols = { ["--pr-cmp-cols" as string]: String(sides.length) };

  return (
    <>
      {(regen.result || regen.error) && (
        <div className="pr-panel">
          {regen.error
            ? <p className="pr-regen-error">{regen.error}</p>
            : <RegenerateOutcome onDismiss={regen.clear} result={regen.result!} />}
        </div>
      )}

      <div className="pr-panel pr-cmp-head">
        <h2 className="pr-h2">{sides.map(s => s.label).join(" → ")}</h2>
        <div className="pr-cmp-why-grid" style={cols}>
          {sides.map(s => (
            <div className="pr-cmp-why-col" key={s.key}>
              <div className="pr-cmp-why-head">
                {s.label}
                {s.focus && <span className="pr-tag">{s.focus}</span>}
                {canWrite && s.arcPosition != null && (
                  <RegenerateButton
                    busy={busyOn(s.arcPosition)}
                    label={targetLabel({ phase: s.arcPosition }, s.label)}
                    onClick={() => ask({ phase: s.arcPosition as number })}
                  />
                )}
              </div>
              <p className="pr-cmp-why">
                {s.why || (s.detailed
                  ? "No reasoning was given for this phase."
                  : "The phase this student last ran. A past phase records set counts only, so reps, load and reasoning show on the suggested phases alone.")}
              </p>
            </div>
          ))}
        </div>
      </div>

      <SetTable flagged={flagged} sides={sides} />

      <div className="pr-panel">
        <h2 className="pr-h2">Findings</h2>
        <Findings variant={variant} />
      </div>

      <div className="pr-panel">
        <h2 className="pr-h2">Session by session</h2>
        {Array.from({ length: sessions }).map((_, i) => (
          <div className="pr-cmp-workout" key={i}>
            <h3 className="pr-cmp-workout-name">
              {sides.map(s => s.workouts[i]?.name).find(Boolean) || `Session ${i + 1}`}
            </h3>
            <div className="pr-cmp-cols" style={cols}>
              {sides.map(s => (
                <div className="pr-cmp-col" key={s.key}>
                  <div className="pr-cmp-col-head">{s.label}</div>
                  <SessionColumn
                    busyBlock={s.arcPosition != null && regen.busy?.phase === s.arcPosition
                      ? regen.busy?.block ?? null : null}
                    detailed={s.detailed}
                    onAsk={canWrite && s.arcPosition != null
                      ? (block: number) => ask({
                        phase: s.arcPosition as number,
                        workout: s.workouts[i]?.name,
                        block,
                      })
                      : undefined}
                    workout={s.workouts[i]}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
